import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOffline } from '../../contexts/OfflineContext';
import { useTheme, THEME_LABELS } from '../../contexts/ThemeContext';
import { SyncStatusPanel, QueueViewer, SyncAccionesIndicator } from '../sync';
import { useMenuItems } from '../../configs/menuConfig';
import { Bell, Home, Logout } from '../icons/IconLibrary';
import CompanySelector from './CompanySelector';
import { isLocalStack } from '../../helpers/envHelpers';
import { ISOTIPO_BLANCO, LOGO_BLANCO } from '../landing/brandAssets';
import packageInfo from '../../../package.json';
const appVersion = packageInfo.version;

// Convierte una ruta en un id de ancla estable: '/animals/new' -> 'animals-new', '/' -> 'home'
const pathToAnchor = (p: string) => {
  const token = p.split('/').filter(Boolean).join('-');
  return token || 'home';
};

interface HeaderProps {
  onMobileMenuToggle?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Header: React.FC<HeaderProps> = (_props) => {
  const { userCompanies, signOut } = useAuth();
  const { menuItems, quickActions } = useMenuItems();
  const { isOnline } = useOffline();
  const [showQueueViewer, setShowQueueViewer] = useState<boolean>(false);
  const [showMobileMenu, setShowMobileMenu] = useState<boolean>(false);
  const [alertsCount, setAlertsCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, cycle } = useTheme();

  // Cierre de sesión: vuelve siempre a la página de entrada (landing pública).
  const handleSignOut = async () => {
    setShowMobileMenu(false);
    await signOut();
    navigate('/');
  };

  useEffect(() => {
    const updateAlerts = async () => {
      try {
        const { alertsService } = await import('../../services/alerts');
        const count = await alertsService.countPendingAlerts();
        setAlertsCount(count);
      } catch (error) {
        console.error('Error loading alerts count:', error);
      }
    };

    updateAlerts();
  }, []);

  return (
    <>
      {/* Header sobrio: Verde Campo sólido (bg-chrome, igual en los dos temas), sin glassmorphism ni animaciones inertes. */}
      <header className="mcd-chrome relative z-20 border-b border-forest-mid/40 bg-chrome text-white">
        <div className="mx-auto w-full px-2 py-2 sm:px-4 sm:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4 transition-opacity hover:opacity-80" title="Inicio">
              {/* Logo oficial (manual p. 14-16): blanco sobre Verde Campo, nunca retipeado.
                  Horizontal a 36 px de alto ≈ 146 px de ancho (mínimo digital 140 px);
                  en celular no entra a ese ancho → isotipo a 32 px (su mínimo). */}
              <img src={ISOTIPO_BLANCO} alt="MiCampoDigital" className="h-8 w-auto sm:hidden" />
              <img src={LOGO_BLANCO} alt="MiCampoDigital" className="hidden h-9 w-auto sm:block" />
              {/* gap-4 = 16 px ≥ área de resguardo (1X = 0,42 × alto ≈ 15 px) */}
              <span className="rounded bg-forest-mid/40 px-1.5 py-0.5 font-mono text-[10px] text-white/70">v{appVersion}</span>
            </Link>

            {/* Badge de entorno: visible sólo con el stack local (ver helpers/envHelpers).
                Si está, lo que hagas (vos o la IA del chat) no toca las cuentas del remoto. */}
            {isLocalStack() && (
              <span
                className="shrink-0 rounded bg-amber-400 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-950 shadow"
                title="Conectado al stack LOCAL de Supabase (Docker) — no toca las cuentas del remoto"
              >
                ⚡ Stack local
              </span>
            )}

            <div className="flex items-center space-x-1 sm:space-x-4">
              {/* Mobile Hamburger Menu Button */}
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="p-1 text-white focus:outline-none md:hidden"
                aria-label="Menú"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {showMobileMenu ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>

              <div className="hidden items-center space-x-4 md:flex">
                {/* Buscador / command palette (⌘K) */}
                <button
                  data-ar-guide="header-buscador"
                  onClick={() => window.dispatchEvent(new Event('mcd:open-command-palette'))}
                  className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/10 px-2.5 py-1.5 text-xs text-white/70 transition-colors hover:bg-white/20 hover:text-white"
                  title="Buscar página o acción (⌘K)"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="hidden xl:inline">Buscar…</span>
                  <kbd className="rounded border border-paper/20 px-1 font-sans text-[10px]">⌘K</kbd>
                </button>

                {/* Selector de empresa activa (solo multi-empresa) */}
                <CompanySelector />

                {/* Sync Status Panel */}
                <div className="hidden sm:block" data-ar-guide="header-sync-status">
                  <SyncStatusPanel setShowQueueViewer={setShowQueueViewer} />
                </div>

                {/* Acciones con ejecución server-side: pendientes de sync / fallidas
                    (spec alta-masiva-sin-caravana §5.5) */}
                <div className="hidden sm:block">
                  <SyncAccionesIndicator />
                </div>

                {/* Modo de visualización (Oficina / Oscuro) — ciclo a nivel app */}
                <button
                  data-ar-guide="header-tema"
                  onClick={cycle}
                  title={`Modo ${THEME_LABELS[theme]} · clic para cambiar (Oficina → Oscuro)`}
                  aria-label={`Cambiar modo de visualización (actual: ${THEME_LABELS[theme]})`}
                  className="inline-flex items-center gap-1.5 rounded bg-forest-mid/30 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-forest-mid/50"
                >
                  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none" />
                  </svg>
                  <span className="hidden lg:inline">{THEME_LABELS[theme]}</span>
                </button>

                {/* Online/Offline Status Indicator */}
                <div
                  data-ar-guide="header-online"
                  className={`hidden items-center space-x-2 rounded px-2 py-1 text-xs font-medium sm:flex sm:px-3 ${
                    isOnline ? 'bg-forest-mid/30 text-white' : 'bg-danger/30 text-white'
                  }`}
                >
                  <div className={`h-2 w-2 rounded-pixel ${isOnline ? 'bg-success' : 'bg-danger'}`} />
                  <span className="hidden md:inline">{isOnline ? 'En línea' : 'Sin conexión'}</span>
                </div>

                {/* Tutorial de inicio (KAN-232): hub de módulos guiados */}
                <button
                  className="rounded-md p-2 transition-colors hover:bg-forest-mid/40"
                  title="Tutorial y guía de inicio"
                  aria-label="Tutorial y guía de inicio"
                  data-ar-guide="header-tutorial"
                  onClick={() => navigate('/tutorial')}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3L1 8l11 5 9-4.09V15h2V8L12 3z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 11.18V15c0 1.66 3.13 3 7 3s7-1.34 7-3v-3.82l-7 3.18-7-3.18z" />
                  </svg>
                </button>

                {/* Alerts Badge */}
                <button
                  className="relative rounded-md p-2 transition-colors hover:bg-forest-mid/40"
                  title="Pendientes y avisos"
                  data-ar-guide="header-alertas"
                  onClick={() => (window.location.href = '/dashboard?alerts=1')}
                >
                  <Bell size={20} />
                  {alertsCount > 0 && (
                    <span data-testid="header-alerts-count" className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-pixel bg-brote text-xs font-bold text-grafito">
                      {alertsCount > 9 ? '9+' : alertsCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {showMobileMenu && (
          <div className="mcd-chrome relative z-50 bg-chrome md:hidden">
            <div className="space-y-1 px-4 py-3">
              {/* Selector de empresa activa (mobile, solo multi-empresa) */}
              {userCompanies.length > 1 && (
                <div className="pb-2">
                  <CompanySelector className="w-full justify-center" />
                </div>
              )}
              {/* Tutorial de inicio (KAN-232) */}
              <Link
                to="/tutorial"
                data-ar-guide="header-tutorial"
                className="flex items-center space-x-3 rounded-md p-2.5 transition-colors hover:bg-forest-mid/40"
                onClick={() => setShowMobileMenu(false)}
              >
                <span className="shrink-0 text-white/70" aria-hidden="true">🎓</span>
                <span className="font-medium">Tutorial de inicio</span>
              </Link>

              {/* Navigation Links */}
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-ar-guide={item.anchor ?? `menu-${pathToAnchor(item.path)}`}
                  className={`flex items-center space-x-3 rounded-md p-2.5 transition-colors ${
                    location.pathname === item.path ? 'bg-brote/15 text-white' : 'text-white/80 hover:bg-forest-mid/40'
                  }`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <span className="shrink-0 text-white/70">{item.icon}</span>
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}

              {/* Quick Actions */}
              <div className="border-t border-forest-mid/40 pt-3">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/50">Acciones rápidas</h3>
                {quickActions.map((action) => (
                  <Link
                    key={action.path}
                    to={action.path}
                    data-ar-guide={`qa-${pathToAnchor(action.path)}`}
                    className="flex items-center space-x-3 rounded-md p-2 text-white/80 transition-colors hover:bg-forest-mid/40"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <span className="shrink-0 text-white/70">{action.icon}</span>
                    <span className="text-sm font-medium">{action.label}</span>
                  </Link>
                ))}
              </div>

              {/* Mobile Sync Controls */}
              <div className="space-y-2 border-t border-forest-mid/40 pt-3">
                <button
                  onClick={() => setShowQueueViewer(true)}
                  className="w-full rounded-md border border-hairline/30 bg-paper/10 px-3 py-2 text-left text-sm text-white transition-colors hover:bg-paper/15"
                >
                  Ver cola de sincronización
                </button>

                <div
                  className={`flex items-center space-x-2 rounded px-2 py-1 text-xs font-medium ${
                    isOnline ? 'bg-forest-mid/30 text-white' : 'bg-danger/30 text-white'
                  }`}
                >
                  <div className={`h-2 w-2 rounded-pixel ${isOnline ? 'bg-success' : 'bg-danger'}`} />
                  <span>{isOnline ? 'En línea' : 'Sin conexión'}</span>
                </div>
              </div>

              {/* Sesión: página de entrada + cierre de sesión, al fondo del menú */}
              <div className="space-y-1 border-t border-forest-mid/40 pt-3">
                <Link
                  to="/"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center space-x-3 rounded-md p-2 text-white/80 transition-colors hover:bg-forest-mid/40"
                >
                  <span className="shrink-0 text-white/70">
                    <Home size={18} />
                  </span>
                  <span className="text-sm font-medium">Página de inicio</span>
                </Link>
                <button
                  onClick={handleSignOut}
                  className="flex w-full items-center space-x-3 rounded-md p-2 text-left text-white/80 transition-colors hover:bg-danger/20"
                >
                  <span className="shrink-0 text-white/70">
                    <Logout size={18} />
                  </span>
                  <span className="text-sm font-medium">Cerrar sesión</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Queue Viewer Modal */}
      <QueueViewer isOpen={showQueueViewer} onClose={() => setShowQueueViewer(false)} />
    </>
  );
};

export default Header;
